import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import QRCode from "qrcode";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePassportById } from "@/hooks/useProductPassport";
import { SparePartLabelPrintMenu } from "@/components/labels/SparePartLabelPrintMenu";
import {
  buildPassportPrintJobsFromPassports,
  resolveSparePartLabelSize,
  useLabelPrinter,
  useLabelPrintingSettings,
} from "@/labels";

export default function PrintPassportLabel() {
  const { t, i18n } = useTranslation("passport");
  const { id } = useParams();
  const { passport, loading, notFound } = usePassportById(id);
  const qrRef = useRef<HTMLCanvasElement>(null);
  const { settings: labelPrintingSettings } = useLabelPrintingSettings();
  const { zebraPrint } = useLabelPrinter();

  const passportPublicUrl = passport
    ? `${window.location.origin}/passport/${passport.id}`
    : "";

  useEffect(() => {
    if (qrRef.current && passportPublicUrl) {
      QRCode.toCanvas(qrRef.current, passportPublicUrl, { width: 220, margin: 1 }, (err) => {
        if (err) console.error("QR generation error:", err);
      });
    }
  }, [passportPublicUrl]);

  useEffect(() => {
    if (!passport) return;
    const t = setTimeout(() => window.print(), 500);
    return () => clearTimeout(t);
  }, [passport]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (notFound || !passport) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        {t("print.notFound")}
      </div>
    );
  }

  const completedDate = new Date(passport.completed_at).toLocaleDateString(i18n.language || "en-GB");

  return (
    <div className="min-h-screen bg-background p-6 print:p-0 print:bg-white">
      <div className="max-w-2xl mx-auto mb-4 flex items-center justify-between print:hidden">
        <p className="text-sm text-muted-foreground">{t("header.labelTitle")} · {passport.serial_number}</p>
        <SparePartLabelPrintMenu
          namespace="passport"
          mode="single"
          variant="default"
          onPrintBrowser={() => window.print()}
          onPrintZebra={async () => {
            if (!passport) return;
            const size = resolveSparePartLabelSize(labelPrintingSettings.defaultLabelSize);
            const jobs = buildPassportPrintJobsFromPassports(
              [passport],
              {
                certifiedHeading: t("header.certified"),
                modelCaption: t("print.model", { defaultValue: "Model" }),
                categoryCaption: t("print.category", { defaultValue: "Category" }),
                completedCaption: t("print.completed", { defaultValue: "Completed" }),
                qualityGradeCaption: t("print.qualityGrade", { defaultValue: "Quality grade" }),
                serialNumberHeading: t("print.serialNumber", { defaultValue: "Serial number" }),
                scanHint: t("print.scanHint", { defaultValue: "Scan for product passport" }),
                locale: i18n.language || "en-GB",
              },
              size,
            );
            await zebraPrint(jobs);
          }}
        />
      </div>

      <div className="max-w-2xl mx-auto bg-card border-2 border-border rounded-2xl p-6 print:border-black print:rounded-none print:shadow-none print:max-w-none">
        <div className="flex items-start gap-6">
          <canvas ref={qrRef} width={220} height={220} className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground print:text-black">
              {t("header.certified")}
            </p>
            {(() => {
              const brand = (passport.brand || "").trim();
              const nameRaw = (passport.appliance_name || passport.category || "").trim();
              const name =
                brand && nameRaw.toLowerCase().startsWith(brand.toLowerCase())
                  ? nameRaw.slice(brand.length).trim()
                  : nameRaw;
              const title = [brand || "—", name].filter(Boolean).join(" ");
              return (
                <h1 className="text-2xl font-bold text-foreground print:text-black mt-1 leading-tight break-words">
                  {title}
                </h1>
              );
            })()}
            <p className="text-base text-foreground print:text-black mt-1">
              {t("fields.model")}: <span className="font-mono">{passport.model_number || "—"}</span>
            </p>
            <p className="text-sm text-muted-foreground print:text-black mt-3">
              {t("fields.category")}: {passport.category || "—"}
            </p>
            <p className="text-sm text-muted-foreground print:text-black">
              {passport.workflow_type === "refurbish" ? t("fields.refurbished") : t("fields.repaired")}: {completedDate}
            </p>
            {passport.quality_grade && (
              <p className="text-sm text-muted-foreground print:text-black">
                {t("fields.qualityGrade")}: <span className="font-bold">{passport.quality_grade}</span>
              </p>
            )}
            <div className="mt-4 pt-3 border-t border-border print:border-black">
              <p className="text-xs text-muted-foreground print:text-black">{t("fields.serialNumber")}</p>
              <p className="font-mono text-lg font-bold text-foreground print:text-black">
                {passport.serial_number}
              </p>
            </div>
            <p className="text-xs text-muted-foreground print:text-black mt-3 break-all">
              {t("footer.scanQr")} {passportPublicUrl}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: auto; margin: 10mm; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
